import { Injectable } from '@angular/core';
import { RoleNames } from '@common/interfaces/permissions';
import { IProjectScope } from '@common/interfaces/projectScope';
import { Topics } from '@common/interfaces/topics';
import { IUser } from '@common/interfaces/user';
import { ID } from '@datorama/akita';
import { ActionService } from '../action/action.service';
import { CrudService } from '../crudService';
import { ProjectScopeStoreStrategy } from '../updateStoreStrategies';
import { UserQuery } from '../user/user.query';
import { ProjectScopeQuery } from './projectScope.query';
import { ProjectScopeStore } from './projectScope.store';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

const ROLE_OUTFLOW: string = 'Outflow';
const GROUP_EVERYONE: string = '#everyone#';

@Injectable({
  providedIn: 'root'
})
export class ProjectScopeService extends CrudService<IProjectScope, ProjectScopeStore, ProjectScopeQuery> {
  constructor(
    public store: ProjectScopeStore,
    public query: ProjectScopeQuery,
    protected actionService: ActionService,
    protected updateStrategy: ProjectScopeStoreStrategy,
    private userQuery: UserQuery
  ) {
    super(Topics.PROJECT_SCOPES, store, query, actionService, updateStrategy);
  }

  getUserTeamsByProject(userId: string, projectId: string): RoleNames[] {
    return this.query.getByProjectId(projectId)?.users.find((scope) => scope.id === userId)?.teams || [];
  }

  isUserInProjectTeams(userId: string, projectId: string, teams: RoleNames[]) {
    const userTeams = this.getUserTeamsByProject(userId, projectId);
    return teams.some((t) => userTeams.includes(t));
  }

  getUserRolesByTeam(userId: string, projectId: string): RoleNames[] {
    return this.query.getByProjectId(projectId)?.users.find((scope) => scope.id === userId)?.roles || [];
  }

  hasUserRoleInProject(userId: string, projectId: string, role: RoleNames) {
    const userRoles = this.getUserRolesByTeam(userId, projectId);
    console.log('roles of user', userId, 'in project', projectId, JSON.stringify(userRoles));
    return userRoles.some((r) => r === role);
  }

  getOutflowUsersByProjectId$(projectId: string): Observable<IUser[]> {
    return this.query.all$.pipe(
      map((scopes) => scopes.find((scope) => scope.projectId === projectId).users),
      map((usersInScope) => {
        const outflowUsersInScope: ID[] = usersInScope
          .filter((scopeUser) => scopeUser.roles.includes(ROLE_OUTFLOW))
          .map((scopedUser) => scopedUser.id);
        const userIds: Set<ID> = new Set(outflowUsersInScope);
        const users: IUser[] = [];

        if (userIds.has(GROUP_EVERYONE)) {
          users.push(...this.userQuery.getAll());
        } else {
          users.push(...this.userQuery.getAll({ filterBy: (user) => userIds.has(user.id) }));
        }
        return users.sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }
}
